import Honeybadger from "@honeybadger-io/js";

/**
 * Pages Honeybadger when a PUT /update_actual_sets persisted NOTHING — every
 * row it was sent was refused and no real data landed.
 *
 * ActualSetsUpsertService refuses any non-skipped row missing a
 * prescription-required field. That is correct — it is how an untouched ghost
 * row is meant to behave — but it used to be invisible: the endpoint answered
 * `head :ok` whether it wrote three rows or none, so a save that did nothing
 * was indistinguishable from one that worked. A client logging "3 x 8-12 @
 * RPE 7" with no weight typed had all three rows refused, moved on believing
 * they were saved, and the workout later completed with weightless backfilled
 * rows that looked like real logged data (workout 2387605).
 *
 * TOTAL drops only. Reporting every drop was too noisy to be useful: a dropped
 * blank row is the NORMAL case here — an untouched position autosaving
 * alongside real ones — so it fired constantly during ordinary logging, and a
 * class that fires constantly gets muted, which lands us back at the
 * invisibility this exists to fix. A drop with other rows still logged is
 * routine and says nothing. A drop with `logged_count === 0` is the save that
 * did nothing, which is the shape worth waking up for.
 *
 * `logged_count` counts NON-SKIPPED rows the server wrote, so a payload of
 * [skipped, blank, blank] still reports — the skip persisted but nothing was
 * logged.
 *
 * Diagnostics only, deliberately: the user is not told. Whether any of this
 * deserves UI is a product decision this does not pre-empt.
 *
 * Returns the number of dropped rows reported (0 when it stayed silent).
 */
export const reportDroppedActualSets = (response, context = {}) => {
  const dropped = response?.data?.dropped_rows;
  if (!Array.isArray(dropped) || dropped.length === 0) return 0;

  // Absent on a response from before this shipped. Stay silent rather than
  // guess: this function exists to cut noise, so "cannot tell whether anything
  // logged" resolves to not paging.
  const loggedCount = response?.data?.logged_count;
  if (typeof loggedCount !== "number") return 0;
  if (loggedCount > 0) return 0;

  Honeybadger.notify("actual_sets save persisted nothing", {
    name: "DroppedActualSets",
    context: {
      ...context,
      droppedCount: dropped.length,
      // [{ priority, missing: [field, ...] }] — `missing` names the fields the
      // prescription required and the row did not carry, which is what tells
      // you whether this was a blank ghost row or a real save that lost data.
      droppedRows: dropped,
    },
  });

  return dropped.length;
};
